import React, { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { specializationOption } from "../../specialMethod/prescriptionSpecialization";

const PracticeForm = ({ formData, onPrev, onNext }) => {
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null); // which specialization input is active

  // Initial values from parent (formData)
  const initialValues = {
    doctors: formData.doctors.map((doctor) => ({ ...doctor })),
    specialization: formData.specialization.map((spec) => spec),
    achievements: formData.achievements.map((ach) => ach),
    experience: formData.experience || "",
    licenceNumber: formData.licenceNumber || "",
    research_journal: formData.research_journal.map((journal) => journal),
    citations: formData.citations.map((citation) => citation),
    referral: formData.referral || "",
  };

  const validationSchema = Yup.object().shape({
    doctors: Yup.array().of(
      Yup.object().shape({
        degree: Yup.string().required("Degree is required"),
        college: Yup.string().required("College is required"),
        year: Yup.string().required("Year is required"),
      })
    ),
    specialization: Yup.array().of(Yup.string()),
    achievements: Yup.array().of(Yup.string()),
    experience: Yup.number().nullable(),
    licenceNumber: Yup.string(),
    research_journal: Yup.array().of(Yup.string()),
    citations: Yup.array().of(Yup.string()),
    referral: Yup.number().nullable(),
  });

  const formik = useFormik({
    initialValues,
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema,
    onSubmit: (values) => {
      onNext(values);
    },
  });

  // Handlers for dynamic arrays
  const addField = () => {
    formik.setValues({
      ...formik.values,
      doctors: [
        ...formik.values.doctors,
        { degree: "", college: "", year: "" },
      ],
    });
  };

  const removeField = (index) => {
    const updatedDoctors = formik.values.doctors.filter((_, i) => i !== index);
    formik.setValues({ ...formik.values, doctors: updatedDoctors });
  };

  const addSection = (fieldName) => {
    const newFieldValue = [...formik.values[fieldName], ""];
    formik.setFieldValue(fieldName, newFieldValue);
  };

  const removeSection = (fieldName, index) => {
    const newFieldValue = formik.values[fieldName].filter(
      (_, idx) => idx !== index
    );
    formik.setFieldValue(fieldName, newFieldValue);
  };

  // Specialization input handlers
  const handleSpecializationChange = (e, index) => {
    const value = e.target.value;
    // Update the specific specialization value
    const newSpecializations = [...formik.values.specialization];
    newSpecializations[index] = value;
    formik.setFieldValue("specialization", newSpecializations);
    setActiveIndex(index);

    // Filter options
    if (value.trim()) {
      const filtered = specializationOption.filter((option) =>
        option.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
      setShowOptions(true);
    } else {
      setFilteredOptions([]);
      setShowOptions(false);
    }
  };

  const handleOptionClick = (optionValue) => {
    const newSpecializations = [...formik.values.specialization];
    newSpecializations[activeIndex] = optionValue;
    formik.setFieldValue("specialization", newSpecializations);
    setShowOptions(false);
    setFilteredOptions([]);
  };

  return (
    <div className="bg-gray-200 pt-4">
      <div className="mx-auto">
        <div className="flex justify-center md:px-6 pb-6">
          <div className="w-full lg:w-7/12 bg-white p-5 rounded-xl">
            <form
              onSubmit={formik.handleSubmit}
              className="px-8 pt-6 pb-8 relative mb-4 bg-white rounded-xl"
              style={{ backgroundColor: "rgb(255,255,255)" }}
            >
              {/* Decorative background blobs */}
              <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute pointer-events-none bottom-0 right-0 w-64 h-64 bg-gradient-to-tl from-orange-400 to-pink-500 rounded-full filter blur-3xl opacity-20 translate-x-1/3 translate-y-1/4"></div>

              <p className="text-teal-500 text-sm font-semibold my-2">QUALIFICATIONS</p>

              {/* Doctors fields */}
              {formik.values.doctors.map((doctor, index) => (
                <div key={index} className="flex sm:flex-row flex-col mb-2">
                  <div className="flex flex-col flex-grow md:mr-4 mb-2">
                    <p className="text-gray-400">Degree</p>
                    <input
                      type="text"
                      name={`doctors[${index}].degree`}
                      value={doctor.degree}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                      placeholder="Degree"
                    />
                    {formik.touched.doctors &&
                      formik.errors.doctors &&
                      formik.errors.doctors[index]?.degree && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.doctors[index].degree}
                        </p>
                      )}
                  </div>
                  <div className="flex flex-col flex-grow md:mr-4 mb-2">
                    <p className="text-gray-400">College</p>
                    <input
                      type="text"
                      name={`doctors[${index}].college`}
                      value={doctor.college}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                      placeholder="College"
                    />
                    {formik.touched.doctors &&
                      formik.errors.doctors &&
                      formik.errors.doctors[index]?.college && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.doctors[index].college}
                        </p>
                      )}
                  </div>
                  <div className="flex flex-col flex-grow md:mr-4">
                    <p className="text-gray-400">Year</p>
                    <select
                      name={`doctors[${index}].year`}
                      value={doctor.year}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
                    >
                      <option value="">Select Year</option>
                      {Array.from({ length: 126 }, (_, i) => 1900 + i).map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    {formik.touched.doctors &&
                      formik.errors.doctors &&
                      formik.errors.doctors[index]?.year && (
                        <p className="text-red-500 text-sm">
                          {formik.errors.doctors[index].year}
                        </p>
                      )}
                  </div>
                  <div className="flex items-center">
                    {formik.values.doctors.length > 1 && (
                      <button
                        type="button"
                        className="text-black px-2 py-1 rounded-md focus:outline-none"
                        onClick={() => removeField(index)}
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex justify-end">
                <div className="mb-10 md:mr-2 text-center">
                  <button
                    className="w-full px-1 py-1 font-bold text-gray-500 bg-blue-500 rounded-lg dark:bg-white dark:text-gray-500 focus:outline-none"
                    type="button"
                    onClick={addField}
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="grid gap-8 mb-6 md:grid-cols-2">
                {/* Specialization */}
                <div>
                  {formik.values.specialization.map((spec, index) => (
                    <div key={index} className="flex flex-col md:flex-row mb-2">
                      <div className="flex flex-col flex-grow md:mr-4 relative">
                        <p className="text-gray-400">Specialization</p>
                        <input
                          type="text"
                          value={spec}
                          onChange={(e) => handleSpecializationChange(e, index)}
                          onBlur={formik.handleBlur}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                          placeholder="Specialization"
                        />
                        {showOptions && activeIndex === index && (
                          <ul className="absolute z-10 top-full left-0 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-40 overflow-y-auto">
                            {filteredOptions.map((option) => (
                              <li
                                key={option.value}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleOptionClick(option.value)}
                              >
                                {option.label}
                              </li>
                            ))}
                          </ul>
                        )}
                        {formik.touched.specialization &&
                          formik.errors.specialization &&
                          formik.errors.specialization[index] && (
                            <p className="text-red-500 text-sm">
                              {formik.errors.specialization[index]}
                            </p>
                          )}
                      </div>
                      <div className="flex items-center">
                        {index > 0 && (
                          <button
                            type="button"
                            className="text-black px-2 rounded-md focus:outline-none"
                            onClick={() => removeSection("specialization", index)}
                          >
                            X
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <div className="mb-4 md:mr-2 text-center">
                      <button
                        className="w-full px-1 py-1 font-bold text-gray-500 bg-blue-500 rounded-lg dark:bg-white dark:text-gray-500 focus:outline-none"
                        type="button"
                        onClick={() => addSection("specialization")}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Achievements */}
                <div>
                  {formik.values.achievements.map((ach, index) => (
                    <div key={index} className="flex flex-col md:flex-row mb-2">
                      <div className="flex flex-col flex-grow">
                        <p className="text-gray-400">Achievement</p>
                        <input
                          type="text"
                          name={`achievements[${index}]`}
                          value={ach}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                          placeholder="Achievement"
                        />
                        {formik.touched.achievements &&
                          formik.errors.achievements &&
                          formik.errors.achievements[index] && (
                            <p className="text-red-500 text-sm">
                              {formik.errors.achievements[index]}
                            </p>
                          )}
                      </div>
                      <div className="flex items-center">
                        {index > 0 && (
                          <button
                            type="button"
                            className="text-black px-2 rounded-md focus:outline-none"
                            onClick={() => removeSection("achievements", index)}
                          >
                            X
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <div className="mb-4 md:mr-2 text-center">
                      <button
                        className="w-full px-1 py-1 font-bold text-gray-500 bg-blue-500 rounded-lg dark:bg-white dark:text-gray-500 focus:outline-none"
                        type="button"
                        onClick={() => addSection("achievements")}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <p className="text-gray-400">Experience</p>
                  <input
                    type="number"
                    name="experience"
                    value={formik.values.experience}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder="Experience (In Year)"
                  />
                  {formik.touched.experience && formik.errors.experience && (
                    <p className="text-red-500 text-sm">{formik.errors.experience}</p>
                  )}
                </div>

                {/* Licence Number */}
                <div>
                  <p className="text-gray-400">Licence Number</p>
                  <input
                    type="text"
                    name="licenceNumber"
                    value={formik.values.licenceNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder="Licence Number"
                  />
                  {formik.touched.licenceNumber && formik.errors.licenceNumber && (
                    <p className="text-red-500 text-sm">{formik.errors.licenceNumber}</p>
                  )}
                </div>

                {/* Research Journal */}
                <div>
                  {formik.values.research_journal.map((journal, index) => (
                    <div key={index} className="flex flex-col md:flex-row mb-2">
                      <div className="flex flex-col flex-grow">
                        <p className="text-gray-400">Research Journal</p>
                        <input
                          type="text"
                          name={`research_journal[${index}]`}
                          value={journal}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                          placeholder="Research journal"
                        />
                        {formik.touched.research_journal &&
                          formik.errors.research_journal &&
                          formik.errors.research_journal[index] && (
                            <p className="text-red-500 text-sm">
                              {formik.errors.research_journal[index]}
                            </p>
                          )}
                      </div>
                      <div className="flex items-center">
                        {index > 0 && (
                          <button
                            type="button"
                            className="text-black px-2 rounded-md focus:outline-none"
                            onClick={() => removeSection("research_journal", index)}
                          >
                            X
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <div className="mb-4 text-center">
                      <button
                        className="px-1 py-1 font-bold text-gray-500 bg-blue-500 rounded-lg dark:bg-white dark:text-gray-500 focus:outline-none"
                        type="button"
                        onClick={() => addSection("research_journal")}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Citations */}
                <div>
                  {formik.values.citations.map((citation, index) => (
                    <div key={index} className="flex flex-col md:flex-row mb-2">
                      <div className="flex flex-col flex-grow">
                        <p className="text-gray-400">Citation</p>
                        <input
                          type="text"
                          name={`citations[${index}]`}
                          value={citation}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                          placeholder="Citation"
                        />
                        {formik.touched.citations &&
                          formik.errors.citations &&
                          formik.errors.citations[index] && (
                            <p className="text-red-500 text-sm">
                              {formik.errors.citations[index]}
                            </p>
                          )}
                      </div>
                      <div className="flex items-center">
                        {index > 0 && (
                          <button
                            type="button"
                            className="text-black px-2 rounded-md focus:outline-none"
                            onClick={() => removeSection("citations", index)}
                          >
                            X
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end">
                    <div className="mb-4 text-center">
                      <button
                        className="px-1 py-1 font-bold text-gray-500 bg-blue-500 rounded-lg dark:bg-white dark:text-gray-500 focus:outline-none"
                        type="button"
                        onClick={() => addSection("citations")}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Referral */}
                <div>
                  <p className="text-gray-400">Referral Mobile no.</p>
                  <input
                    type="number"
                    name="referral"
                    value={formik.values.referral}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    placeholder="Referral Mobile no."
                  />
                  {formik.touched.referral && formik.errors.referral && (
                    <p className="text-red-500 text-sm">{formik.errors.referral}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <div className="mb-4 mr-2 text-center">
                  <button
                    className="w-full px-4 py-2 text-orange-500 font-bold border border-orange-500 bg-white rounded-full focus:outline-none"
                    type="button"
                    onClick={onPrev}
                  >
                    Back
                  </button>
                </div>
                <div className="mb-4 text-center">
                  <button
                    className="w-full px-4 py-2 font-bold text-white bg-orange-500 rounded-full hover:bg-orange-700 focus:outline-none"
                    type="submit"
                  >
                    Preview
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeForm;